/**
 * @fileoverview ItemInstance schemas for issue assignment validation and type definitions
 */

import { z } from '@hono/zod-openapi';
import { createListResponseSchema } from './common.js';

/**
 * Valid workflow flags for item instances
 */
export const ITEM_INSTANCE_FLAGS = ['new', 'review', 'followup'];

/**
 * Valid newsletter section categories for sorting items within issues
 */
export const ITEM_INSTANCE_CATEGORIES = [
  'news',
  'tutorial',
  'reads',
  'multimedia',
  'aws',
  'social',
  'tools',
  'security',
  'events',
];

// Base ItemInstance schema without defaults (for partial updates)
const BaseItemInstanceSchema = z.object({
  // ID format: {hash}:{ulid} (composite format for efficient storage and lookup)
  // hash: 12 char base-36 string, ulid: 26 char ULID, separated by colon
  id: z
    .string()
    .regex(/^[0-9a-z]{12}:[0-9A-HJKMNP-TV-Z]{26}$/)
    .openapi({
      description: 'Composite identifier for the item instance (format: hash:ulid)',
      example: 'abc123xyz789:01K3HJRSZMTPGGKDN3GKDR29MY',
    }),
  hash: z
    .string()
    .regex(/^[0-9a-z]{12}$/)
    .openapi({
      description: 'Base-36 hash of the canonical item (links to main item record)',
      example: 'abc123xyz789',
    }),
  created: z.string().datetime().openapi({
    description: 'ISO 8601 timestamp when the instance was created',
    example: '2025-01-15T10:00:00Z',
  }),
  modified: z.string().datetime().openapi({
    description: 'ISO 8601 timestamp when the instance was last modified',
    example: '2025-01-15T10:30:00Z',
  }),
  issueNumber: z.number().int().positive().openapi({
    description: 'Issue number this instance is assigned to (positive integer)',
    example: 42,
  }),
  // Instance-specific overrides
  title: z.string().optional().openapi({
    description: 'Custom title override for this issue assignment',
    example: 'Understanding Serverless (Issue #42)',
  }),
  blurb: z.string().optional().openapi({
    description: 'Custom blurb override for this issue assignment',
    example: 'A great introduction to serverless concepts',
  }),
  notes: z.string().optional().openapi({
    description: 'Instance-specific notes',
    example: 'Featured article for this issue',
  }),
  // Denormalized fields from canonical Item for easy querying
  issues: z.array(z.number()).optional().openapi({
    description: 'Array of issue numbers this item has been assigned to',
    example: [334, 335, 336],
  }),
  summary: z.string().optional().openapi({
    description: 'Summary from the canonical item (denormalized). Prefers AI summary if available.',
    example: 'Learn how to use Zod for runtime type validation...',
  }),
  url: z.string().url().openapi({
    description: 'URL from the canonical item (denormalized)',
    example: 'https://example.com/posts/understanding-zod',
  }),
  published: z.string().datetime().openapi({
    description: 'Published date from the canonical item (denormalized)',
    example: '2024-01-15T09:00:00Z',
  }),
  authors: z.array(z.string()).openapi({
    description: 'Authors from the canonical item (denormalized)',
    example: ['John Doe', 'Jane Smith'],
  }),
  domain: z.string().openapi({
    description: 'Domain from the canonical item (denormalized)',
    example: 'example.com',
  }),
  type: z.enum(['article', 'video', 'audio', 'image', 'social', 'unknown']).openapi({
    description: 'Content type from the canonical item (denormalized)',
    example: 'article',
  }),
  timeToRead: z.number().nullable().openapi({
    description: 'Reading time from the canonical item (denormalized)',
    example: 5,
  }),
  qualityScore: z.number().nullable().optional().openapi({
    description: 'AI quality score from canonical item (denormalized, 1-5 scale with decimals)',
    example: 3.7,
  }),
  relevanceScore: z.number().nullable().optional().openapi({
    description: 'AI relevance score from canonical item (denormalized, 1-5 scale with decimals)',
    example: 4.2,
  }),
  category: z
    .enum(['news', 'tutorial', 'reads', 'multimedia', 'aws', 'social', 'tools', 'security', 'events'])
    .nullable()
    .optional()
    .openapi({
      description: 'Newsletter section category for sorting within an issue (null for uncategorized)',
      example: 'tutorial',
    }),
  position: z.string().optional().openapi({
    description:
      'LexoRank position string for ordering items within a category (e.g., "m00000")',
    example: 'm00000',
  }),
  flag: z
    .enum(['new', 'review', 'followup'])
    .nullable()
    .optional()
    .openapi({
      description: 'Workflow flag for this instance (new, review, followup)',
      example: 'new',
    }),
  stats: z
    .object({
      uniqueClicks: z.number().int().nonnegative().optional().openapi({
        description: 'Number of unique visitors who clicked this item in this issue',
        example: 142,
      }),
      totalClicks: z.number().int().nonnegative().optional().openapi({
        description: 'Total click count for this item in this issue',
        example: 287,
      }),
      clickRate: z.number().min(0).max(100).optional().openapi({
        description: 'Percentage of issue recipients who clicked this item',
        example: 12.5,
      }),
      lastClickedAt: z.string().datetime().optional().openapi({
        description: 'ISO 8601 timestamp of most recent click',
        example: '2025-01-15T14:30:00Z',
      }),
    })
    .optional()
    .openapi({
      description: 'Click tracking statistics for this item instance (placeholder for future implementation)',
    }),
});

// Full ItemInstance schema with defaults (for creation and storage)
export const ItemInstanceSchema = BaseItemInstanceSchema.extend({
  issues: BaseItemInstanceSchema.shape.issues.default([]),
  timeToRead: BaseItemInstanceSchema.shape.timeToRead.default(null),
}).openapi('ItemInstance');

export const CreateItemInstanceSchema = ItemInstanceSchema.omit({
  id: true,
  created: true,
  modified: true,
})
  .strict()
  .openapi('CreateItemInstance');

// Use base schema for updates to avoid applying defaults to unset fields
export const UpdateItemInstanceSchema = BaseItemInstanceSchema.partial()
  .omit({ id: true, hash: true, created: true, modified: true, issueNumber: true })
  .strict()
  .openapi('UpdateItemInstance');

export const ItemInstanceListResponseSchema = createListResponseSchema(
  ItemInstanceSchema,
  'instances',
  'ItemInstanceListResponse'
);

/**
 * JSDoc type definition for ItemInstance - Issue assignment record
 * @typedef {Object} ItemInstanceType
 * @property {string} id - Unique identifier (ULID)
 * @property {string} hash - Base-36 hash linking to canonical item
 * @property {string} created - ISO 8601 timestamp when instance was created
 * @property {string} modified - ISO 8601 timestamp when instance was last modified
 * @property {number} issueNumber - Issue number this instance is assigned to
 * @property {number[]} [issues] - Array of issue numbers this item has been assigned to (denormalized)
 * @property {string} [title] - Custom title override for this assignment
 * @property {string} [blurb] - Custom blurb override for this assignment
 * @property {string} [notes] - Instance-specific notes
 * @property {string} [summary] - Summary from canonical item (denormalized, prefers AI summary)
 * @property {string} url - URL from canonical item (denormalized)
 * @property {string} published - Published date from canonical item (denormalized)
 * @property {string[]} authors - Authors from canonical item (denormalized)
 * @property {string} domain - Domain from canonical item (denormalized)
 * @property {'article'|'video'|'audio'|'image'|'social'|'unknown'} type - Content type (denormalized)
 * @property {number|null} timeToRead - Reading time from canonical item (denormalized)
 * @property {number|null} [qualityScore] - AI quality score from canonical item (denormalized, 1-5 scale with decimals)
 * @property {number|null} [relevanceScore] - AI relevance score from canonical item (denormalized, 1-5 scale with decimals)
 * @property {'news'|'tutorial'|'reads'|'multimedia'|'aws'|'social'|'tools'|'security'|'events'|null} [category] - Newsletter section category for sorting (null for uncategorized)
 * @property {string} [position] - LexoRank position string for ordering within a category
 * @property {'new'|'review'|'followup'|null} [flag] - Workflow flag for this instance
 * @property {Object} [stats] - Click tracking statistics (placeholder for future implementation)
 * @property {number} [stats.uniqueClicks] - Number of unique visitors who clicked
 * @property {number} [stats.totalClicks] - Total click count
 * @property {number} [stats.clickRate] - Percentage of issue recipients who clicked (0-100)
 * @property {string} [stats.lastClickedAt] - ISO 8601 timestamp of most recent click
 */

export const ItemInstanceType = /** @type {ItemInstanceType} */ ({});
